import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import { calendarEventsTrackingIds } from '../Calendar/EventsPanel/CalendarEvents/calendar-events-tracking-ids';
import { DeleteAppointmentButton } from '../Calendar/EventsPanel/CalendarEvents/delete-appointment-button';

export const PanelCloseButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button
      variant='secondary'
      onClick={onClick}
      css={closeButtonStyle}
      size='large'
      aria-label='Close panel'
      iconName='x'
    />
  );
};

export const PanelHeader = ({ title }: { title: string }) => {
  return (
    <header css={headerStyle}>
      <Heading level={2} css={{ lineHeight: 1.25 }}>
        {title}
      </Heading>
    </header>
  );
};

export const PanelFooter = ({
  onDelete,
  onCancel,
  onSave,
  saveLabel,
  saveDisabled = false,
  hideDelete = true,
}: {
  saveLabel?: string;
  saveDisabled?: boolean;
  hideDelete?: boolean;
  onDelete: () => void;
  onCancel: () => void;
  onSave: () => void;
}) => {
  const { t } = useTranslation('schedule');
  return (
    <footer css={[footerStyle, !hideDelete && { justifyContent: 'space-between' }]}>
      {!hideDelete && <DeleteAppointmentButton style={{ position: 'unset' }} onDelete={onDelete} />}
      <div css={{ display: 'flex', gap: theme.spacing(1) }}>
        <Button
          variant='secondary'
          onClick={onCancel}
          trackingId={calendarEventsTrackingIds.cancelAppointmentEventButton}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant='primary'
          onClick={onSave}
          disabled={saveDisabled}
          trackingId={calendarEventsTrackingIds.saveAppointmentEventButton}
        >
          {saveLabel || t('Save')}
        </Button>
      </div>
    </footer>
  );
};

export const PanelFormField = ({ label, children }: { label: string; children: React.ReactNode }) => {
  return (
    <div>
      <Text weight='bold' color='light' size='medium'>
        {label}
      </Text>
      {children}
    </div>
  );
};

const closeButtonStyle = css({
  padding: theme.spacing(1),
  borderRadius: theme.borderRadius.medium,
  transition: 'background-color 200ms',
  '&:hover': {
    backgroundColor: theme.colors.neutral10,
  },
  alignSelf: 'flex-end',
  margin: theme.spacing(0.5),
});

const headerStyle = css({
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  borderTop: `1px solid ${theme.colors.neutral20}`,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 3),
});

const footerStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  padding: theme.spacing(2, 3),
  width: '100%',
});
